import { useEffect, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Box } from '@mui/material';

import config from '@occc/config';

import {
  MAP_HEIGHT,
  MAP_WIDTH,
  MAP_ZOOM,
  MAP_FALLBACK_ZOOM,
  FALLBACK_MAP_CENTER,
} from './constants';

const apiLoaderConfig = {
  id: 'google-map-script',
  googleMapsApiKey: config.google.mapsApiKey || '',
};

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  gestureHandling: 'none',
  clickableIcons: false,
  draggable: false,
  scrollwheel: false,
  disableDoubleClickZoom: true,
  keyboardShortcuts: false,
};

type StaticMapProps = {
  coordinates?: google.maps.LatLngLiteral | null;
  width?: string | number;
  height?: string | number;
  zoom?: number;
  markerIcon?: string | google.maps.Icon | google.maps.Symbol | null;
};

export const StaticMap = ({
  coordinates,
  width = MAP_WIDTH,
  height = MAP_HEIGHT,
  zoom = MAP_ZOOM,
  markerIcon,
}: StaticMapProps) => {
  const { isLoaded } = useJsApiLoader(apiLoaderConfig);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (mapRef.current && coordinates) {
      mapRef.current.setCenter(coordinates);
      mapRef.current.setZoom(zoom);
    }
  }, [coordinates, zoom]);

  if (!isLoaded) {
    return <Box sx={{ width, height, bgcolor: 'grey.100' }} />;
  }

  return (
    <Box sx={{ position: 'relative', width, height }}>
      {!coordinates && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'grey.100', zIndex: 1 }} />
      )}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height }}
        center={coordinates ?? FALLBACK_MAP_CENTER}
        zoom={coordinates ? zoom : MAP_FALLBACK_ZOOM}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        options={MAP_OPTIONS}
      >
        {coordinates && <Marker position={coordinates} icon={markerIcon ?? undefined} />}
      </GoogleMap>
    </Box>
  );
};
